import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, CheckCircle, Clock, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { SEOHead } from '../components/seo/SEOHead';
import { 
  CaseCategory, 
  CaseCategoryLabels, 
  CaseStatus,
  SAMPLE_CASES,
  filterCasesByCategory,
  searchCases 
} from '../models/caseArchive';

/**
 * Case Archive Page (V2 Feature)
 * 
 * Displays anonymized case outcomes.
 * Enable via V2_ENABLE_CASE_ARCHIVE=true
 */

const CaseCard = ({ caseData }) => {
  const statusIcon = caseData.status === CaseStatus.APPROVED 
    ? <CheckCircle className="h-4 w-4 text-green-600" />
    : <Clock className="h-4 w-4 text-amber-600" />;

  return (
    <div 
      className="bg-white border border-gray-200 p-6 hover:border-[hsl(215,50%,23%)] transition-colors"
      data-testid={`case-card-${caseData.caseId}`}
    >
      <div className="flex items-start justify-between mb-3">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          {CaseCategoryLabels[caseData.category]}
        </span>
        <div className="flex items-center gap-1.5">
          {statusIcon}
          <span className="text-sm font-medium text-gray-700">{caseData.outcome}</span>
        </div>
      </div>
      
      <h3 className="font-bold text-[hsl(215,50%,23%)] text-lg mb-2">
        {caseData.outcome}
      </h3>
      
      <p className="text-gray-600 text-sm mb-4">
        {caseData.summary}
      </p>
      
      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{caseData.timeline}</span>
        <span>Case #{caseData.caseId}</span>
      </div>
    </div>
  );
};

export default function CaseArchive() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // In production, this would fetch from API
  const cases = SAMPLE_CASES;
  
  const filteredCases = useMemo(() => {
    let result = filterCasesByCategory(cases, selectedCategory);
    result = searchCases(result, searchQuery);
    return result;
  }, [cases, selectedCategory, searchQuery]);

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: CaseCategory.RECORD_SUSPENSION, label: 'Record Suspension' },
    { value: CaseCategory.US_ENTRY_WAIVER, label: 'U.S. Entry Waivers' },
    { value: CaseCategory.INTERNATIONAL_FILING, label: 'International Filings' },
  ];

  return (
    <>
      <SEOHead page="caseArchive" />
      
      <div data-testid="case-archive-page">
        {/* Hero Section */}
        <section className="bg-[hsl(215,50%,23%)] text-white py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h1 className="text-2xl md:text-3xl font-bold mb-3 text-white">Case Archive</h1>
            <p className="text-white/80 max-w-2xl">
              Browse anonymized case outcomes from our administrative assistance services. 
              All identifying information has been removed to protect client privacy.
            </p>
          </div>
        </section>

        {/* Filters */}
        <section className="bg-[hsl(210,20%,96%)] py-6 border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-4">
              {/* Search */}
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search cases..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="case-search-input"
                />
              </div>
              
              {/* Category Filter */}
              <div className="flex gap-2 flex-wrap">
                {categories.map(cat => (
                  <button
                    key={cat.value}
                    onClick={() => setSelectedCategory(cat.value)}
                    className={`px-4 py-2 text-sm rounded-sm transition-colors ${
                      selectedCategory === cat.value
                        ? 'bg-[hsl(215,50%,23%)] text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                    data-testid={`filter-${cat.value}`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Case Grid */}
        <section className="gov-section">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            {filteredCases.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredCases.map(caseData => (
                  <CaseCard key={caseData.caseId} caseData={caseData} />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 mb-4">No cases match your search criteria.</p>
                <Button 
                  onClick={() => { setSearchQuery(''); setSelectedCategory('all'); }}
                  className="gov-btn-secondary"
                >
                  Clear Filters
                </Button>
              </div>
            )}
          </div>
        </section>

        {/* CTA */}
        <section className="gov-section-alt">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center">
            <h2 className="text-xl font-bold text-[hsl(215,50%,23%)] mb-4">
              Ready to Start Your Case?
            </h2>
            <p className="text-gray-600 mb-6">
              Complete our eligibility assessment to determine if you qualify for our services.
            </p>
            <Link to="/eligibility">
              <Button className="gov-btn-primary">
                Check Eligibility
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
